// FolderTreePicker — 이동 모달의 폴더 고르기(들여쓴 트리).
// mode='note'   : 노트 이동 — 맨 위 «미분류» + 트리. 지금 폴더·읽기 권한 폴더는 고를 수 없다.
// mode='folder' : 폴더 위치 옮기기 — 맨 위 «최상위»(소유자만) + 트리. 자기·하위·다른 사람 트리·3단계 초과는 막는다.
// 🔴 단추 글자는 폴더 이름만 둔다(접근 이름 = 이름 — jest `getByRole('button', { name: '영업' })`).
//    고를 수 없는 행은 disabled + title 에 이유를 적는다. 최종 판정은 서버가 한다.
import { IconFolder, IconFolderOpen } from '@tabler/icons-react';
import { MEETING_THEME, moveItemStyle } from '../../utils/meetingUtil';
import { MAX_FOLDER_DEPTH, canWriteFolder, flattenTree, isInSubtree, subtreeHeight } from '../../utils/meetingFolderTree';

const T = MEETING_THEME;
const nameStyle = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const same = (a, b) => a != null && b != null && String(a) === String(b);

export default function FolderTreePicker({
  folders,
  mode = 'note',
  currentId,            // note: 노트가 지금 든 폴더(null=미분류 · undefined=없음/여러 개) · folder: 쓰지 않는다
  movingFolder = null,  // folder: 옮기는 폴더
  canMoveToTop = false, // folder: «최상위» 허용(소유자만)
  onPick,
}) {
  const list = Array.isArray(folders) ? folders : [];
  const items = flattenTree(list);
  const movingHeight = mode === 'folder' && movingFolder ? subtreeHeight(list, movingFolder.id) : 1;

  const reasonFor = (f, depth) => {
    if (mode === 'folder') {
      if (!movingFolder) return '옮길 폴더가 없습니다.';
      if (isInSubtree(list, movingFolder.id, f.id)) return '자기 자신이나 하위 폴더로 옮길 수 없습니다.';
      if (!canWriteFolder(f)) return '읽기 권한만 있는 폴더입니다.';
      if (f.owner_id != null && movingFolder.owner_id != null && String(f.owner_id) !== String(movingFolder.owner_id)) {
        return '다른 사람의 폴더 트리로는 옮길 수 없습니다.';
      }
      if (same(f.id, movingFolder.parent_id)) return '지금 있는 위치입니다.';
      if (depth + movingHeight > MAX_FOLDER_DEPTH) return '옮기면 3단계를 넘습니다.';
      return '';
    }
    if (!canWriteFolder(f)) return '읽기 권한만 있는 폴더입니다.';
    if (currentId !== undefined && same(f.id, currentId)) return '지금 있는 폴더입니다.';
    return '';
  };

  let topLabel = '미분류';
  let topReason = '';
  let topCurrent = false;
  if (mode === 'folder') {
    topLabel = '최상위';
    topCurrent = !!movingFolder && movingFolder.parent_id == null;
    if (topCurrent) topReason = '지금 있는 위치입니다.';
    else if (!canMoveToTop) topReason = '최상위로 옮기기는 폴더 소유자만 할 수 있습니다.';
  } else {
    topCurrent = currentId === null;
    if (topCurrent) topReason = '지금 있는 폴더입니다.';
  }

  const itemStyle = (active, disabled, depth) => ({
    ...moveItemStyle(active),
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    minWidth: 0,
    padding: `10px 12px 10px ${12 + (depth - 1) * 16}px`,   // moveItemStyle 의 padding 을 단계만큼 들여쓴다
    opacity: disabled && !active ? 0.45 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
  });

  return (
    <div data-testid="folder-tree-picker" style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '55vh', overflowY: 'auto' }}>
      <button type="button" disabled={!!topReason} title={topReason || undefined}
        onClick={() => { if (!topReason) onPick?.(null); }}
        style={itemStyle(topCurrent, !!topReason, 1)}>
        <IconFolderOpen size={16} style={{ flexShrink: 0 }} /><span style={nameStyle}>{topLabel}</span>
      </button>
      {items.map((it) => {
        const reason = reasonFor(it.folder, it.depth);
        const active = mode === 'note' ? (currentId !== undefined && same(it.folder.id, currentId))
          : same(it.folder.id, movingFolder?.parent_id);
        return (
          <button key={it.key} type="button" disabled={!!reason} title={reason || undefined}
            data-depth={it.depth}
            onClick={() => { if (!reason) onPick?.(it.folder.id); }}
            style={itemStyle(active, !!reason, it.depth)}>
            <IconFolder size={16} style={{ flexShrink: 0, color: T.color.iconLine }} /><span style={nameStyle}>{it.folder.name}</span>
          </button>
        );
      })}
    </div>
  );
}
